import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { faFileExclamation } from "@fortawesome/pro-regular-svg-icons"
import { faVideo } from "@fortawesome/pro-solid-svg-icons"
import WordThumbnail from "../../../static/assets/images/microsoft-word-icon.svg"
import ExcelThumbnail from "../../../static/assets/images/microsoft-excel-icon.svg"
import PowerpointThumbnail from "../../../static/assets/images/microsoft-powerpoint-icon.svg"
import PDFThumbnail from "../../../static/assets/images/pdf-icon.svg"
import useCheckIfMobile from "../hooks/useCheckIfMobile"
import convertMimeToExtension from "./convertMimeToExtension"
import "./FileDisplayer.css"

/**
 * Displays a user-uploaded file
 * @param {userFile} file
 * @param {boolean} isThumbnail
 */
const FileDisplayer = ({file, isThumbnail}) => {
  const { isMobile } = useCheckIfMobile()

  const fileURL = file.fileURL
  const fileType = file.fileType
  const thumbnailURL = file.thumbnailURL
  const c = isThumbnail ? "-thumbnail" : ""

  const isImage = ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml", "image/apng"].includes(fileType)
  const isVideo = fileType === "video/mp4"
  const isPDF = fileType === "application/pdf"
  const isWord = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(fileType)
  const isExcel = ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(fileType)
  const isPowerpoint = ["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"].includes(fileType)
  const isMicrosoftFile = isWord || isExcel || isPowerpoint

  let fileDisplayElement = <FontAwesomeIcon className="loading-spinner" icon={faSpinner} spin size="lg" />

  if (file.errorMessage) {
    fileDisplayElement = (
      <>
        <FontAwesomeIcon icon={faFileExclamation} color="#8B0000" size="4x"/>
        <div className="error-message">
          ERROR
        </div>
      </>
    )
  } else if (file.isLoading) {
  } else if (isImage) {
    fileDisplayElement = <img className={`image${c}`} src={fileURL} alt="user uploaded content"/>
  } else if (isVideo && isThumbnail && isMobile) {
    // many mobile browsers render empty box instead of a video's thumbnail
    fileDisplayElement = <FontAwesomeIcon icon={faVideo} size="4x"/>
  } else if (isVideo){
    fileDisplayElement = <video className={`video${c}`} src={fileURL} {...(isThumbnail ? {} : {controls: true})}/>
  } else if (isThumbnail && thumbnailURL) {
    fileDisplayElement = <img className="file-thumbnail" src={file.thumbnailURL} alt="thumbnail of user uploaded file"/>
  } else if (isThumbnail && isPDF) {
    fileDisplayElement = <img className={`pdf-icon`} src={PDFThumbnail} alt="PDF icon"/> 
  } else if (isThumbnail && isWord) {
    fileDisplayElement = <img className={`word-icon`} src={WordThumbnail} alt="Microsoft Word icon"/> 
  } else if (isThumbnail && isExcel) {
    fileDisplayElement = <img className={`excel-icon`} src={ExcelThumbnail} alt="Microsoft Excel icon"/> 
  } else if (isThumbnail && isPowerpoint) {
    fileDisplayElement = <img className={`powerpoint-icon`} src={PowerpointThumbnail} alt="Microsoft Powerpoint icon"/> 
  } else if (isPDF) {
    // on mobile browsers that can't display pdfs, just display the thumbnail
    fileDisplayElement = (
      <object className="pdf" data={fileURL} width="60%" height="100%">
        {thumbnailURL ? (
          <img className="image-thumbnail" src={file.thumbnailURL} alt="thumbnail of user uploaded PDF"/>
        ) : (
          <img className={`pdf-icon`} src={PDFThumbnail} alt="PDF icon"/> 
        )}
      </object>
    )
  } else if (isMicrosoftFile) {
    // For more info, check the documentation of the downloadStoryboardFile firebase function
    const fileExtension = convertMimeToExtension(file.fileType)
    const linkToFile = `https://us-central1-${process.env.GATSBY_FIREBASE_PROJECT_ID}.cloudfunctions.net/downloadStoryboardFile/${file.userId}/${file.fileId}.${fileExtension}`
    fileDisplayElement = (
      <>
      <div id="storyboard-file-loading-spinner-container">
        <FontAwesomeIcon className="loading-spinner" icon={faSpinner} spin size="lg" />
      </div>
      <iframe 
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(linkToFile)}`} 
        className="microsoft-web-viewer"
        height="100%"
        width="100%" 
        frameborder='0'
        onLoad={() => {
          if (typeof document !== "undefined") {
            const loadingSpinnerElement = document.getElementById("storyboard-file-loading-spinner-container")
            loadingSpinnerElement.style.display = "none"
          }
        }}
      />
      </>
    )
  }

  return (
    <div className="file-displayer">
      {fileDisplayElement}
    </div>
  )
}

export default FileDisplayer
